import { getFieldGroupInfo } from 'lib/fields';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import {
  FieldField,
  AssessmentTemplate,
  FieldGroupWithCalculatedInfo,
  OrganisationListItem,
} from 'store/organisations/types';
import { State } from 'store/types';

type OrganisationsStateTree = Omit<Partial<State>, 'organisations'> &
  Pick<State, 'organisations'>;

const selectStore = (state: OrganisationsStateTree) => state.organisations;

/** LIST ORGANISATIONS */

export const selectOrganisationsList = createSelector(
  selectStore,
  (state) => state.list,
);

export const selectOrganisationIds = createSelector(
  selectOrganisationsList,
  (list) => Object.keys(list),
);

export const selectOrganisationListItem = createCachedSelector(
  selectOrganisationsList,
  (_: OrganisationsStateTree, id: string) => id,
  (list, id): OrganisationListItem | undefined => list[id],
)((_: OrganisationsStateTree, id: string) => id);

export const selectAssessmentTemplateIdsByOrganisationIds = createSelector(
  selectOrganisationsList,
  (list) =>
    Object.fromEntries(
      Object.entries(list).map(([organisationId, organisation]) => [
        organisationId,
        organisation.assessmentTemplateIds,
      ]),
    ),
);

export const selectOrganisationFilterOptions = createSelector(
  selectOrganisationsList,
  (list): { id: string; name: string }[] =>
    naturalOrderBy(
      Object.values(list).map((organisation) => ({
        id: organisation.id,
        name: organisation.details.name,
      })),
      'name',
    ),
);

/** LOADED ORGANISATIONS */

const selectOrganisations = createSelector(
  selectStore,
  (state) => state.loaded,
);

export const selectIsOrganisationLoaded = createCachedSelector(
  selectOrganisations,
  (_: OrganisationsStateTree, id: string) => id,
  (organisations, id) => !!organisations[id],
)((_: OrganisationsStateTree, id: string) => id);

export const selectOrganisation = createCachedSelector(
  selectOrganisations,
  (_: OrganisationsStateTree, id: string) => id,
  (organisations, id) => {
    if (!organisations[id]) {
      throw new Error(
        'Organisation must be loaded from local storage before selecting it',
      );
    }
    return organisations[id]!;
  },
)((_: OrganisationsStateTree, id: string) => id);

export const selectOrganisationOrderedFieldSections = createCachedSelector(
  selectOrganisation,
  (organisation) =>
    organisation.fieldSectionIds.map(
      (sectionId) => organisation.fieldSections[sectionId]!,
    ),
)((_: OrganisationsStateTree, id: string) => id);

export const selectOrganisationFields = createCachedSelector(
  selectOrganisationOrderedFieldSections,
  (sections): Record<string, FieldField> =>
    sections.reduce<Record<string, FieldField>>((acc, section) => {
      Object.values(section.groups).forEach((group) => {
        Object.values(group.fields).forEach((field) => {
          acc[field.id] = field;
        });
      });
      return acc;
    }, {}),
)((_: OrganisationsStateTree, organisationId: string) => organisationId);

export const selectOrganisationFieldGroups = createCachedSelector(
  selectOrganisationOrderedFieldSections,
  (sections): Record<string, FieldGroupWithCalculatedInfo> =>
    sections.reduce<Record<string, FieldGroupWithCalculatedInfo>>(
      (acc, section) =>
        Object.values(section.groups).reduce((acc1, group) => {
          acc1[group.id] = {
            ...group,
            calculatedInfo: getFieldGroupInfo(group, group.fields),
          };
          return acc1;
        }, acc),
      {},
    ),
)((_: OrganisationsStateTree, organisationId: string) => organisationId);

export const selectOrganisationFieldGroup = createCachedSelector(
  selectOrganisationFieldGroups,
  (_, __, groupId) => groupId,
  (groups, groupId): FieldGroupWithCalculatedInfo | undefined =>
    groups[groupId],
)(
  (_: OrganisationsStateTree, organisationId: string, groupId: string) =>
    `${organisationId}-${groupId}`,
);

export const selectOrganisationAssessmentTemplate = createSelector(
  (_: OrganisationsStateTree, organisationId: string) =>
    selectOrganisation(_, organisationId),
  (_: OrganisationsStateTree, organisationId: string, templateId: string) =>
    templateId,
  (organisation, templateId): AssessmentTemplate =>
    organisation.assessmentTemplates[templateId]!,
);
